import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { reportBuilderService } from '../../services/reportBuilderService';
import type { SavedReportConfig, QueryResult, ReportChartType, ResultViewMode, ChartFieldMapping, ColumnMeta, ScopingType } from '../../types/reportBuilder';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';
import { WidgetChart } from '@t-one-internal/design-system';
import { ChartTypeSelector } from '@t-one-internal/design-system';
import { Table, ChartBar, ArrowLeft, PencilSimple, Trash, ArrowClockwise, CircleNotch, WarningCircle, CloudArrowUp } from '@phosphor-icons/react';
import { PublishAsWidgetDialog } from '../../components/report-builder';
import { FIELD_COLORS } from '../../components/report-builder/fieldEncoding';
import { useUserAccess } from '../../hooks/useUserAccess';
import { getAuthorizedScopes, SCOPING_LABELS } from '../../utils/reportBuilderScopingUtils';

const ALLOWED_CHART_TYPES: ReportChartType[] = ['bar', 'column', 'line', 'area', 'pie', 'donut', 'radar'];

// Map report builder chart types → widget chart types (different naming conventions)
const CHART_TYPE_TO_WIDGET: Record<string, string> = {
  column: 'bar',          // vertical bars
  bar: 'horizontal_bar',  // horizontal bars
  radar: 'bar',           // no radar in widgets, fallback
};

function formatCellValue(value: unknown, col: ColumnMeta): string {
  if (value === null || value === undefined) return '-';
  switch (col.display_format) {
    case 'percentage': return `${Number(value).toFixed(1)}%`;
    case 'currency': return `$${Number(value).toLocaleString()}`;
    case 'decimal_2': return Number(value).toFixed(2);
    case 'date_short': return value ? new Date(String(value)).toLocaleDateString() : '-';
    case 'datetime': return value ? new Date(String(value)).toLocaleString() : '-';
    default: return String(value);
  }
}

export default function ReportBuilderViewerPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const access = useUserAccess();
  const authorizedScopes = getAuthorizedScopes(access);

  const [report, setReport] = useState<SavedReportConfig | null>(null);
  const [results, setResults] = useState<QueryResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [queryLoading, setQueryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ResultViewMode>('table');
  const [chartType, setChartType] = useState<ReportChartType>('column');
  const [chartFieldMapping, setChartFieldMapping] = useState<ChartFieldMapping | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 100 });
  const [activeScopingType, setActiveScopingType] = useState<ScopingType | null>(null);

  const fetchAndRun = useCallback(async (reportConfig: SavedReportConfig, offset = 0, limit = 100, scopeOverride?: ScopingType | null) => {
    setQueryLoading(true);
    setError(null);
    try {
      const scopeToUse = scopeOverride !== undefined ? scopeOverride : activeScopingType;
      // ISU-6: Strip any stale scoping_type from the saved query_spec and always
      // apply the explicit scope so we never accidentally send a scope the current
      // user is not authorized for.
      const { scoping_type: _savedScope, ...baseSpec } = reportConfig.query_spec;
      const spec = { ...baseSpec, offset, limit, ...(scopeToUse ? { scoping_type: scopeToUse } : {}) };
      const result = await reportBuilderService.executeQuery(spec);
      setResults(result);

      // Auto-detect chart field mapping from query results.
      // Always re-derive from actual columns to avoid stale saved mappings
      // referencing columns that no longer exist in the result set.
      const resultKeys = new Set(result.columns.map(c => c.key));
      const dimensionCols = result.columns.filter(c => c.is_dimension);
      const measureCols = result.columns.filter(c => c.is_measure);
      const timeGroupCol = dimensionCols.find(c => c.is_time_grouping);
      const nameField = timeGroupCol?.key ?? dimensionCols[0]?.key ?? '';
      const valueField = measureCols[0]?.key ?? '';
      if (nameField && valueField && resultKeys.has(nameField) && resultKeys.has(valueField)) {
        setChartFieldMapping({ nameField, valueField });
      } else {
        setChartFieldMapping(null);
      }
    } catch (e: any) {
      const msg = e?.response?.data?.details || e?.response?.data?.error || (e instanceof Error ? e.message : 'Query failed');
      setError(msg);
    } finally {
      setQueryLoading(false);
    }
  }, [activeScopingType]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    reportBuilderService.getSavedReport(id).then(async (config) => {
      setReport(config);
      const savedScope = config.query_spec.scoping_type ?? null;
      // ISU-6: Validate saved scope against the current user's authorized scopes.
      // If the saved scope is not one the user is allowed to use, fall back to
      // their highest-privilege authorized scope to avoid 403 errors.
      let effectiveScope = savedScope;
      if (authorizedScopes.length > 0) {
        if (!effectiveScope || !authorizedScopes.includes(effectiveScope)) {
          effectiveScope = authorizedScopes[0];
        }
      }
      setActiveScopingType(effectiveScope);
      if (config.visualization_config) {
        setChartType(config.visualization_config.chartType);
        setChartFieldMapping(config.visualization_config.fieldMapping);
      }
      setLoading(false);
      await fetchAndRun(config, 0, 100, effectiveScope);
    }).catch(() => {
      setError('Failed to load report');
      setLoading(false);
    });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open publish dialog when navigated with ?publish=true
  useEffect(() => {
    if (searchParams.get('publish') === 'true' && report) {
      setPublishDialogOpen(true);
      searchParams.delete('publish');
      setSearchParams(searchParams, { replace: true });
    }
  }, [report, searchParams, setSearchParams]);

  const handleRerun = () => {
    if (report) fetchAndRun(report, paginationModel.page * paginationModel.pageSize, paginationModel.pageSize, activeScopingType);
  };

  const handleScopeChange = (newScope: ScopingType) => {
    setActiveScopingType(newScope);
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    if (report) fetchAndRun(report, 0, paginationModel.pageSize, newScope);
  };

  const handlePaginationChange = useCallback((model: GridPaginationModel) => {
    setPaginationModel(model);
    if (report) fetchAndRun(report, model.page * model.pageSize, model.pageSize, activeScopingType);
  }, [report, fetchAndRun, activeScopingType]);

  const handleDelete = async () => {
    if (!id) return;
    await reportBuilderService.deleteSavedReport(id);
    navigate('/analytics/report-builder');
  };

  const handleChartMode = () => {
    setViewMode('chart');
  };

  const gridColumns: GridColDef[] = useMemo(() => {
    if (!results?.columns) return [];
    return results.columns.map((col) => ({
      field: col.key,
      headerName: col.label,
      flex: 1,
      minWidth: 120,
      sortable: col.sortable,
      headerClassName: col.is_dimension ? 'rb-dim-header' : col.is_measure ? 'rb-meas-header' : '',
      valueFormatter: (value: unknown) => formatCellValue(value, col),
    }));
  }, [results?.columns]);

  const rows = useMemo(() => {
    if (!results?.data) return [];
    return results.data.map((row, idx) => ({ id: idx, ...row }));
  }, [results?.data]);

  const rowCount = results?.pagination?.total ?? results?.query_stats?.row_count ?? 0;

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <CircleNotch size={32} className="animate-spin text-blue-500" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-gray-500">Report not found</p>
      </div>
    );
  }

  if (report.restricted) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/analytics/report-builder')}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">{report.name}</h1>
          </div>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <WarningCircle size={40} className="mx-auto text-gray-400" />
            <p className="mt-3 text-sm font-medium text-gray-700">Report not available</p>
            <p className="mt-1 text-xs text-gray-500">This report uses modules that are not included in your current subscription.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/analytics/report-builder')}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{report.name}</h1>
              <div className="mt-0.5 flex items-center gap-2">
                {report.description && <span className="text-sm text-gray-500">{report.description}</span>}
                <div className="flex gap-1">
                  {report.model_keys.map(key => (
                    <span key={key} className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">{key}</span>
                  ))}
                  {activeScopingType && (
                    <span className="inline-flex rounded-full bg-purple-50 px-2 py-0.5 text-[10px] font-medium text-purple-700">
                      {SCOPING_LABELS[activeScopingType]}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRerun}
              disabled={queryLoading}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <ArrowClockwise size={16} className={queryLoading ? 'animate-spin' : ''} /> Re-run
            </button>
            <button
              onClick={() => navigate(`/analytics/report-builder/${id}/edit`)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <PencilSimple size={16} /> Edit
            </button>
            <button
              onClick={() => setPublishDialogOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <CloudArrowUp size={16} /> Publish as Widget
            </button>
            <button
              onClick={() => setDeleteConfirmOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50"
            >
              <Trash size={16} /> Delete
            </button>
          </div>
        </div>
      </div>

      {/* View toggle bar */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border border-gray-300 bg-white">
              <button
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-1.5 rounded-l-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  viewMode === 'table' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Table className="h-4 w-4" /> Table
              </button>
              <button
                onClick={handleChartMode}
                className={`flex items-center gap-1.5 rounded-r-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  viewMode === 'chart' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <ChartBar className="h-4 w-4" /> Chart
              </button>
            </div>
            {viewMode === 'chart' && (
              <ChartTypeSelector
                currentChartType={chartType}
                allowedChartTypes={ALLOWED_CHART_TYPES}
                onChartTypeChange={(type) => setChartType(type as ReportChartType)}
              />
            )}
          </div>
          <div className="flex items-center gap-3">
            {authorizedScopes.length > 1 && (
              <select
                value={activeScopingType ?? ''}
                onChange={(e) => handleScopeChange(e.target.value as ScopingType)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {authorizedScopes.map((scope) => (
                  <option key={scope} value={scope}>{SCOPING_LABELS[scope]}</option>
                ))}
              </select>
            )}
            {results && (
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span>{results.query_stats.row_count} rows</span>
                {results.query_stats.query_time_ms > 0 && <span>{results.query_stats.query_time_ms}ms</span>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-hidden p-4">
        {error ? (
          <div className="flex h-full items-center justify-center">
            <div className="flex items-center gap-2 text-red-600">
              <WarningCircle size={20} />
              <span className="text-sm">{error}</span>
            </div>
          </div>
        ) : queryLoading && !results ? (
          <div className="flex h-full items-center justify-center">
            <CircleNotch size={32} className="animate-spin text-blue-500" />
          </div>
        ) : results ? (
          viewMode === 'chart' && chartFieldMapping ? (
            <div className="h-[500px] w-full rounded-lg border border-gray-200 bg-white p-4">
              <WidgetChart
                type={chartType}
                data={results.data}
                config={{
                  keys: {
                    name: chartFieldMapping.nameField,
                    value: chartFieldMapping.valueField,
                  },
                  labels: {
                    name: results.columns.find(c => c.key === chartFieldMapping.nameField)?.label,
                    value: results.columns.find(c => c.key === chartFieldMapping.valueField)?.label,
                  },
                  options: { show_legend: true },
                }}
                onPointClick={() => setViewMode('table')}
              />
            </div>
          ) : (
            <div className="h-full w-full">
              <DataGrid
                rows={rows}
                columns={gridColumns}
                loading={queryLoading}
                paginationMode="server"
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationChange}
                pageSizeOptions={[25, 50, 100, 250]}
                disableRowSelectionOnClick
                sx={{
                  [`& .rb-dim-header`]: { backgroundColor: FIELD_COLORS.dimension.headerBg },
                  [`& .rb-meas-header`]: { backgroundColor: FIELD_COLORS.measure.headerBg },
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  backgroundColor: '#fff',
                  height: '100%',
                }}
              />
            </div>
          )
        ) : null}
      </div>

      {/* Delete confirmation */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-base font-semibold text-gray-900">Delete Report</h3>
            <p className="mt-2 text-sm text-gray-500">Are you sure you want to delete "{report.name}"? This action cannot be undone.</p>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setDeleteConfirmOpen(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Publish as Widget dialog */}
      <PublishAsWidgetDialog
        open={publishDialogOpen}
        onOpenChange={setPublishDialogOpen}
        reportId={id!}
        reportName={report.name}
        reportDescription={report.description}
        chartType={CHART_TYPE_TO_WIDGET[chartType] || chartType}
      />
    </div>
  );
}
